import { AddressAutocompleteInput } from '../../common/AddressAutocompleteInput';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Chip,
  Switch,
  FormHelperText,
  Alert,
  IconButton,
} from '@mui/joy';
import {
  FloppyDisk as Save,
  X as Close,
  Plus as Add,
  Trash as Delete,
  MapPin as LocationOn,
  User as Person,
  Buildings as Business,
  Phone,
  Envelope as Email,
  LinkedinLogo as LinkedIn,
  FacebookLogo as Facebook,
  Cake,
  Heart as Favorite,
  Clock,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { Contact, ContactFormData } from '../../../types/crm';
import {
  useCreateContact,
  useUpdateContact,
  useContactSources,
  useContact,
  useCreateContactSource,
  useUsers,
} from '../../../hooks/useCrm';
import { TagPicker } from '../../tags/TagPicker';
import { LeadSourcePicker } from './LeadSourcePicker';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api';

interface ContactFormProps {
  contactId?: number; // If provided, form is in edit mode
  onSave?: (contact: Contact) => void;
  onCancel?: () => void;
  /** Section to show first (e.g. jump straight to Tags). */
  initialSection?: SectionKey;
}

interface AddressFormData {
  address_type: string;
  street_address_1: string;
  street_address_2?: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  latitude?: number;
  longitude?: number;
  is_primary: boolean;
  is_billing: boolean;
  is_verified: boolean;
  full_address?: string;
}

const initialAddress: AddressFormData = {
  address_type: 'primary',
  street_address_1: '',
  street_address_2: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: 'US',
  is_primary: true,
  is_billing: false,
  is_verified: false,
};

const contactTypeOptions = [
  { value: 'lead', label: 'Lead' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'customer', label: 'Customer' },
  { value: 'past_customer', label: 'Past Customer' },
  { value: 'vendor', label: 'Vendor' },
];

const contactMethodOptions = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'text', label: 'Text' },
  { value: 'any', label: 'Any Method' },
];

const addressTypeOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'home', label: 'Home' },
  { value: 'billing', label: 'Billing' },
  { value: 'property', label: 'Property' },
  { value: 'mailing', label: 'Mailing' },
  { value: 'other', label: 'Other' },
];

export type SectionKey = 'basic' | 'contact' | 'web' | 'dates' | 'tags' | 'addresses' | 'source';

const FORM_SECTIONS: { key: SectionKey; label: string }[] = [
  { key: 'basic', label: 'Basic information' },
  { key: 'contact', label: 'Contact information' },
  { key: 'web', label: 'Web & social media' },
  { key: 'dates', label: 'Important dates' },
  { key: 'tags', label: 'Tags' },
  { key: 'addresses', label: 'Addresses' },
  { key: 'source', label: 'Source & assignment' },
];

// Validation-error keys (or prefixes) that belong to each section — drives
// the red dot in the section nav and the jump-to-error on submit.
const SECTION_ERROR_FIELDS: Record<SectionKey, string[]> = {
  basic: ['first_name', 'last_name'],
  contact: ['email', 'secondary_email', 'phone', 'secondary_phone'],
  web: ['website', 'linkedin_url', 'facebook_url'],
  dates: ['birthday', 'anniversary'],
  tags: [],
  addresses: ['address_'],
  source: ['source'],
};

export const ContactForm: React.FC<ContactFormProps> = ({
  contactId,
  onSave,
  onCancel,
  initialSection,
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    first_name: '',
    last_name: '',
    title: '',
    company_name: '',
    contact_type: 'lead',
    email: '',
    phone: '',
    secondary_email: '',
    secondary_phone: '',
    preferred_contact_method: 'email',
    do_not_call: false,
    do_not_email: false,
    do_not_text: false,
    website: '',
    linkedin_url: '',
    facebook_url: '',
    birthday: undefined,
    anniversary: undefined,
    addresses: [],
  });

  const [addresses, setAddresses] = useState<AddressFormData[]>([]);
  const [websiteProtocol, setWebsiteProtocol] = useState<'http://' | 'https://'>('https://');
  const [websiteDomain, setWebsiteDomain] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [activeSection, setActiveSection] = useState<SectionKey>(initialSection ?? 'basic');

  const sectionHasError = (key: SectionKey) =>
    Object.keys(errors).some((errKey) =>
      SECTION_ERROR_FIELDS[key].some((field) => errKey.startsWith(field))
    );

  // API hooks
  const { data: existingContact } = useContact(contactId || 0);
  const { data: sources = [] } = useContactSources();
  const { data: users = [] } = useUsers();
  const { data: availableTags = [], refetch: refetchTags, isLoading: tagsLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      console.log('[ContactForm] Fetching tags...');
      const response = await apiClient.getTags();
      console.log('[ContactForm] Tags response:', response.data);
      return response.data;
    },
  });

  // Debug logging for tags
  React.useEffect(() => {
    console.log('[ContactForm] Available tags updated:', {
      count: availableTags.length,
      tags: availableTags,
      loading: tagsLoading
    });
  }, [availableTags, tagsLoading]);

  // Handle creating new tags
  const handleCreateTag = async (name: string, color: string) => {
    const response = await apiClient.createTag(name, color);
    await refetchTags(); // Refresh the tags list
    return response.data;
  };

  // Handle creating new lead sources inline
  const createSourceMutation = useCreateContactSource();
  const handleCreateSource = async (name: string) => {
    return createSourceMutation.mutateAsync(name);
  };

  const createContactMutation = useCreateContact();
  const updateContactMutation = useUpdateContact();

  const isEditMode = !!contactId;
  const isLoading = createContactMutation.isPending || updateContactMutation.isPending;

  // Load existing contact data for edit mode
  useEffect(() => {
    if (isEditMode && existingContact) {
      setFormData({
        first_name: existingContact.first_name || '',
        last_name: existingContact.last_name || '',
        title: existingContact.title || '',
        company_name: existingContact.company_name || '',
        contact_type: existingContact.contact_type,
        email: existingContact.email || '',
        phone: existingContact.phone || '',
        secondary_email: existingContact.secondary_email || '',
        secondary_phone: existingContact.secondary_phone || '',
        preferred_contact_method: existingContact.preferred_contact_method || 'email',
        do_not_call: existingContact.do_not_call || false,
        do_not_email: existingContact.do_not_email || false,
        do_not_text: existingContact.do_not_text || false,
        website: existingContact.website || '',
        linkedin_url: existingContact.linkedin_url || '',
        facebook_url: existingContact.facebook_url || '',
        birthday: existingContact.birthday || undefined,
        anniversary: existingContact.anniversary || undefined,
        source_id: existingContact.source?.id,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        assigned_to: typeof existingContact.assigned_to === 'object' ? (existingContact.assigned_to as any)?.id : existingContact.assigned_to,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        referred_by: typeof existingContact.referred_by === 'object' ? (existingContact.referred_by as any)?.id : existingContact.referred_by,
        addresses: [],
      });

      // Load addresses
      if (existingContact.addresses && existingContact.addresses.length > 0) {
        setAddresses(existingContact.addresses.map(addr => ({
          address_type: addr.address_type,
          street_address_1: addr.street_address_1,
          street_address_2: addr.street_address_2 || '',
          city: addr.city,
          state_province: addr.state_province,
          postal_code: addr.postal_code,
          country: addr.country,
          latitude: addr.latitude,
          longitude: addr.longitude,
          is_primary: addr.is_primary,
          is_billing: addr.is_billing,
          is_verified: addr.is_verified,
          full_address: addr.full_address,
        })));
      }

      // Parse website into protocol and domain
      const existingWebsite = existingContact.website || '';
      if (existingWebsite.startsWith('http://')) {
        setWebsiteProtocol('http://');
        setWebsiteDomain(existingWebsite.slice(7));
      } else if (existingWebsite.startsWith('https://')) {
        setWebsiteProtocol('https://');
        setWebsiteDomain(existingWebsite.slice(8));
      } else if (existingWebsite) {
        setWebsiteProtocol('https://');
        setWebsiteDomain(existingWebsite);
      } else {
        setWebsiteProtocol('https://');
        setWebsiteDomain('');
      }

      // Load tags - ensure we have tag IDs
      const tagIds = existingContact.tags ? existingContact.tags.map(tag => tag.id) : [];
      setSelectedTagIds(tagIds);
    }
    // Intentionally omit availableTags / sources from deps: their references
    // change when React Query updates other caches (e.g. after creating a new
    // lead source) and would otherwise reset the entire form back to the
    // backend's stored values, blowing away in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, existingContact]);

  // Handle form field changes
  const handleFieldChange = (field: keyof ContactFormData, value: string | boolean | number | null | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle address changes
  const handleAddressChange = (index: number, field: keyof AddressFormData, value: any) => {
    setAddresses(prev => prev.map((addr, i) => 
      i === index ? { ...addr, [field]: value } : addr
    ));
  };

  // Add new address
  const addAddress = () => {
    setAddresses(prev => [...prev, { ...initialAddress }]);
  };

  // Remove address
  const removeAddress = (index: number) => {
    setAddresses(prev => prev.filter((_, i) => i !== index));
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.secondary_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.secondary_email)) {
      newErrors.secondary_email = 'Please enter a valid secondary email address';
    }

    // Validate addresses
    addresses.forEach((addr, index) => {
      if (addr.street_address_1.trim() && !addr.city.trim()) {
        newErrors[`address_${index}_city`] = 'City is required';
      }
      if (addr.street_address_1.trim() && !addr.state_province.trim()) {
        newErrors[`address_${index}_state`] = 'State/Province is required';
      }
    });

    setErrors(newErrors);
    // Jump to the first section that has a problem so errors are never
    // hidden behind an inactive section.
    const firstError = Object.keys(newErrors)[0];
    if (firstError) {
      const section = FORM_SECTIONS.find(({ key }) =>
        SECTION_ERROR_FIELDS[key].some((field) => firstError.startsWith(field))
      );
      if (section) setActiveSection(section.key);
    }
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      website: websiteDomain.trim() ? `${websiteProtocol}${websiteDomain.trim()}` : '',
      addresses: addresses.filter(addr => addr.street_address_1.trim()),
      tag_ids: selectedTagIds,
    };

    try {
      if (isEditMode && contactId) {
        const updatedContact = await updateContactMutation.mutateAsync({
          id: contactId,
          data: submitData as ContactFormData,
        });
        onSave?.(updatedContact);
      } else {
        const newContact = await createContactMutation.mutateAsync(submitData as ContactFormData);
        onSave?.(newContact);
      }
    } catch (error) {
      console.error('[ContactForm] Submit error:', error);
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', maxHeight: '92vh', minHeight: 0 }}>
      {/* Header */}
      <Box
        sx={{
          px: 3, py: 2,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        <Box>
          <Typography level="h3">{isEditMode ? 'Edit contact' : 'New contact'}</Typography>
          {(formData.first_name || formData.last_name || formData.company_name) && (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {[`${formData.first_name} ${formData.last_name}`.trim(), formData.company_name]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Box>
        <IconButton variant="outlined" size="sm" onClick={onCancel}>
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Section navigation */}
          <Box
            sx={{
              width: 230, flexShrink: 0, p: 2,
              borderRight: '1px solid', borderColor: 'divider',
              display: { xs: 'none', md: 'block' }, overflowY: 'auto',
            }}
          >
            <Typography
              level="body-xs"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.tertiary', fontWeight: 'lg', mb: 1 }}
            >
              Sections
            </Typography>
            <Stack spacing={0.25}>
              {FORM_SECTIONS.map(({ key, label }) => {
                const selected = activeSection === key;
                const badge = key === 'tags' ? selectedTagIds.length : key === 'addresses' ? addresses.length : null;
                return (
                  <Button
                    key={key}
                    variant={selected ? 'soft' : 'plain'}
                    color={selected ? 'primary' : 'neutral'}
                    size="sm"
                    onClick={() => setActiveSection(key)}
                    sx={{
                      justifyContent: 'flex-start',
                      fontWeight: selected ? 'lg' : 'md',
                      '& .MuiButton-endDecorator': { ml: 'auto' },
                    }}
                    endDecorator={
                      sectionHasError(key) ? (
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'danger.500' }} />
                      ) : badge != null ? (
                        <Chip size="sm" variant="soft" color="neutral">{badge}</Chip>
                      ) : null
                    }
                  >
                    {label}
                  </Button>
                );
              })}
            </Stack>
          </Box>

          {/* Section content */}
          <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
            {/* Mobile section picker */}
            <Select
              size="sm"
              value={activeSection}
              onChange={(_, value) => value && setActiveSection(value as SectionKey)}
              sx={{ mb: 2, display: { xs: 'flex', md: 'none' } }}
            >
              {FORM_SECTIONS.map(({ key, label }) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
            <Stack spacing={3}>
              {/* Basic Information */}
              {activeSection === 'basic' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 2 }}>Basic Information</Typography>
                  
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <FormControl required error={!!errors.first_name} sx={{ flex: 1 }}>
                      <FormLabel>First Name</FormLabel>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => handleFieldChange('first_name', e.target.value)}
                        startDecorator={<Person />}
                        placeholder="Enter first name"
                      />
                      {errors.first_name && <FormHelperText>{errors.first_name}</FormHelperText>}
                    </FormControl>

                    <FormControl required error={!!errors.last_name} sx={{ flex: 1 }}>
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => handleFieldChange('last_name', e.target.value)}
                        placeholder="Enter last name"
                      />
                      {errors.last_name && <FormHelperText>{errors.last_name}</FormHelperText>}
                    </FormControl>
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Title</FormLabel>
                      <Input
                        value={formData.title}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        placeholder="Job title or position"
                      />
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Company</FormLabel>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => handleFieldChange('company_name', e.target.value)}
                        startDecorator={<Business />}
                        placeholder="Company name"
                      />
                    </FormControl>
                  </Stack>

                  <FormControl sx={{ width: { xs: '100%', md: '200px' } }}>
                    <FormLabel>Contact Type</FormLabel>
                    <Select
                      value={formData.contact_type}
                      onChange={(_, value) => handleFieldChange('contact_type', value)}
                    >
                      {contactTypeOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
              )}

              {/* Contact Information */}
              {activeSection === 'contact' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 2 }}>Contact Information</Typography>
                  
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <FormControl error={!!errors.email} sx={{ flex: 1 }}>
                      <FormLabel>Primary Email</FormLabel>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        startDecorator={<Email />}
                        placeholder="email@example.com"
                      />
                      {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Primary Phone</FormLabel>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                        startDecorator={<Phone />}
                        placeholder="(555) 123-4567"
                      />
                    </FormControl>
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <FormControl error={!!errors.secondary_email} sx={{ flex: 1 }}>
                      <FormLabel>Secondary Email</FormLabel>
                      <Input
                        type="email"
                        value={formData.secondary_email}
                        onChange={(e) => handleFieldChange('secondary_email', e.target.value)}
                        startDecorator={<Email />}
                        placeholder="alternate@example.com"
                      />
                      {errors.secondary_email && <FormHelperText>{errors.secondary_email}</FormHelperText>}
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Secondary Phone</FormLabel>
                      <Input
                        type="tel"
                        value={formData.secondary_phone}
                        onChange={(e) => handleFieldChange('secondary_phone', e.target.value)}
                        startDecorator={<Phone />}
                        placeholder="(555) 987-6543"
                      />
                    </FormControl>
                  </Stack>

                  <FormControl sx={{ width: { xs: '100%', md: '200px' }, mb: 2 }}>
                    <FormLabel>Preferred Contact Method</FormLabel>
                    <Select
                      value={formData.preferred_contact_method}
                      onChange={(_, value) => handleFieldChange('preferred_contact_method', value)}
                    >
                      {contactMethodOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>

                  <Stack direction="row" spacing={3}>
                    <FormControl>
                      <Switch
                        checked={formData.do_not_call}
                        onChange={(e) => handleFieldChange('do_not_call', e.target.checked)}
                      />
                      <FormLabel>Do not call</FormLabel>
                    </FormControl>

                    <FormControl>
                      <Switch
                        checked={formData.do_not_email}
                        onChange={(e) => handleFieldChange('do_not_email', e.target.checked)}
                      />
                      <FormLabel>Do not email</FormLabel>
                    </FormControl>

                    <FormControl>
                      <Switch
                        checked={formData.do_not_text}
                        onChange={(e) => handleFieldChange('do_not_text', e.target.checked)}
                      />
                      <FormLabel>Do not text</FormLabel>
                    </FormControl>
                  </Stack>
                </CardContent>
              </Card>
              )}

              {/* Web & Social */}
              {activeSection === 'web' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 2 }}>Web & Social Media</Typography>
                  
                  <Stack spacing={2}>
                    <FormControl error={!!errors.website}>
                      <FormLabel>Website</FormLabel>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl sx={{ minWidth: '110px' }}>
                          <Select
                            value={websiteProtocol}
                            onChange={(_, value) => {
                              if (value) setWebsiteProtocol(value as 'http://' | 'https://');
                            }}
                          >
                            <Option value="https://">https://</Option>
                            <Option value="http://">http://</Option>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                          <Input
                            value={websiteDomain}
                            onChange={(e) => setWebsiteDomain(e.target.value)}
                            placeholder="example.com"
                          />
                        </FormControl>
                      </Box>
                      {errors.website && <FormHelperText>{errors.website}</FormHelperText>}
                    </FormControl>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>LinkedIn Profile</FormLabel>
                        <Input
                          value={formData.linkedin_url}
                          onChange={(e) => handleFieldChange('linkedin_url', e.target.value)}
                          startDecorator={<LinkedIn />}
                          placeholder="https://linkedin.com/in/username"
                        />
                      </FormControl>

                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Facebook Profile</FormLabel>
                        <Input
                          value={formData.facebook_url}
                          onChange={(e) => handleFieldChange('facebook_url', e.target.value)}
                          startDecorator={<Facebook />}
                          placeholder="https://facebook.com/username"
                        />
                      </FormControl>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
              )}

              {/* Personal Dates */}
              {activeSection === 'dates' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 2 }}>Important Dates</Typography>
                  
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Birthday</FormLabel>
                      <Input
                        type="date"
                        value={formData.birthday || ''}
                        onChange={(e) => handleFieldChange('birthday', e.target.value || undefined)}
                        startDecorator={<Cake />}
                      />
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Anniversary</FormLabel>
                      <Input
                        type="date"
                        value={formData.anniversary || ''}
                        onChange={(e) => handleFieldChange('anniversary', e.target.value || undefined)}
                        startDecorator={<Favorite />}
                      />
                    </FormControl>
                  </Stack>
                </CardContent>
              </Card>
              )}

              {/* Tags */}
              {activeSection === 'tags' && (
              <Card variant="outlined">
                <CardContent>
                  <FormControl>
                    <FormLabel>Tags</FormLabel>
                    {tagsLoading ? (
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        Loading tags...
                      </Typography>
                    ) : (
                      <TagPicker
                        label=""
                        value={selectedTagIds}
                        onChange={setSelectedTagIds}
                        availableTags={availableTags}
                        onCreateTag={handleCreateTag}
                      />
                    )}
                    {!tagsLoading && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {availableTags.length} tags available, {selectedTagIds.length} selected
                      </Typography>
                    )}
                  </FormControl>
                </CardContent>
              </Card>
              )}

              {/* Addresses */}
              {activeSection === 'addresses' && (
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography level="title-sm">Addresses</Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="sm"
                      startDecorator={<Add />}
                      onClick={addAddress}
                    >
                      Add Address
                    </Button>
                  </Stack>

                  <Stack spacing={3}>
                    {addresses.map((address, index) => (
                      <Card key={index} variant="soft">
                        <CardContent>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography level="body-sm" fontWeight="md">
                              Address {index + 1}
                            </Typography>
                            <IconButton
                              variant="outlined"
                              color="danger"
                              size="sm"
                              onClick={() => removeAddress(index)}
                            >
                              <Delete />
                            </IconButton>
                          </Stack>

                          <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <FormControl sx={{ width: { xs: '100%', sm: '150px' } }}>
                                <FormLabel>Type</FormLabel>
                                <Select
                                  value={address.address_type}
                                  onChange={(_, value) => handleAddressChange(index, 'address_type', value)}
                                >
                                  {addressTypeOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                      {option.label}
                                    </Option>
                                  ))}
                                </Select>
                              </FormControl>

                              <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Street Address 1</FormLabel>
                                <AddressAutocompleteInput
                                  value={address.street_address_1}
                                  onChange={(value) => setAddresses(previous => previous.map((item, i) => i === index ? { ...item, street_address_1: value, latitude: undefined, longitude: undefined } : item))}
                                  onAddressSelect={(parsed) => { setAddresses(previous => previous.map((item, i) => i === index ? { ...item, street_address_1: parsed.address_1, city: parsed.city, state_province: parsed.state, postal_code: parsed.zip_code, latitude: parsed.lat, longitude: parsed.lng } : item)); setErrors(previous => ({ ...previous, [`address_${index}_city`]: '', [`address_${index}_state`]: '' })); }}
                                />
                              </FormControl>
                            </Stack>

                            <FormControl>
                              <FormLabel>Street Address 2</FormLabel>
                              <Input
                                value={address.street_address_2}
                                onChange={(e) => handleAddressChange(index, 'street_address_2', e.target.value)}
                                placeholder="Apt, Suite, Unit (optional)"
                              />
                            </FormControl>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <FormControl error={!!errors[`address_${index}_city`]} sx={{ flex: 2 }}>
                                <FormLabel>City</FormLabel>
                                <Input
                                  value={address.city}
                                  onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                                  placeholder="City"
                                />
                                {errors[`address_${index}_city`] && (
                                  <FormHelperText>{errors[`address_${index}_city`]}</FormHelperText>
                                )}
                              </FormControl>

                              <FormControl error={!!errors[`address_${index}_state`]} sx={{ flex: 1 }}>
                                <FormLabel>State/Province</FormLabel>
                                <Input
                                  value={address.state_province}
                                  onChange={(e) => handleAddressChange(index, 'state_province', e.target.value)}
                                  placeholder="State"
                                />
                                {errors[`address_${index}_state`] && (
                                  <FormHelperText>{errors[`address_${index}_state`]}</FormHelperText>
                                )}
                              </FormControl>

                              <FormControl sx={{ flex: 1 }}>
                                <FormLabel>ZIP/Postal Code</FormLabel>
                                <Input
                                  value={address.postal_code}
                                  onChange={(e) => handleAddressChange(index, 'postal_code', e.target.value)}
                                  placeholder="12345"
                                />
                              </FormControl>
                            </Stack>

                            <Stack direction="row" spacing={2}>
                              <FormControl>
                                <Switch
                                  checked={address.is_primary}
                                  onChange={(e) => handleAddressChange(index, 'is_primary', e.target.checked)}
                                />
                                <FormLabel>Primary address</FormLabel>
                              </FormControl>

                              <FormControl>
                                <Switch
                                  checked={address.is_billing}
                                  onChange={(e) => handleAddressChange(index, 'is_billing', e.target.checked)}
                                />
                                <FormLabel>Billing address</FormLabel>
                              </FormControl>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}

                    {addresses.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <LocationOn size={48} color="var(--joy-palette-text-secondary)" style={{ marginBottom: 8 }} />
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          No addresses added yet
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
              )}

              {/* Source and Assignment */}
              {activeSection === 'source' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 2 }}>Source & Assignment</Typography>
                  
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <LeadSourcePicker
                        value={formData.source_id}
                        onChange={(id) => handleFieldChange('source_id', id)}
                        availableSources={sources}
                        onCreateSource={handleCreateSource}
                      />
                    </Box>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Assigned To</FormLabel>
                      <Select
                        value={formData.assigned_to ?? null}
                        onChange={(_, value) => handleFieldChange('assigned_to', value as number | null)}
                        placeholder="Unassigned"
                        startDecorator={<Person />}
                      >
                        <Option value={null}>Unassigned</Option>
                        {users.map((u) => (
                          <Option key={u.id} value={u.id}>{u.full_name}</Option>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </CardContent>
              </Card>
              )}

              {/* Error Display */}
              {(createContactMutation.error || updateContactMutation.error) && (
                <Alert color="danger">
                  Failed to {isEditMode ? 'update' : 'create'} contact. Please try again.
                </Alert>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3, py: 1.5,
            borderTop: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 2,
          }}
        >
          <Typography level="body-sm" startDecorator={<Clock size={14} />} sx={{ color: 'text.tertiary' }}>
            {isEditMode && (existingContact as any)?.updated_at
              ? `Last saved ${formatDistanceToNow(new Date((existingContact as any).updated_at), { addSuffix: true })}`
              : 'Not saved yet'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" color="neutral" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="solid" loading={isLoading} startDecorator={<Save />}>
            {isEditMode ? 'Save changes' : 'Create Contact'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};